<template>
  <div class="tml-product-info">
    <product-title
      :title="product.name"
      :price="titlePrice"
      :sale-price="titleSalePrice"
      :show-percentage="product.isBundle"
      :to-price="showRange ? maxOptionPrice : undefined"
      :class="descriptionBelowCta ? 'mb-4' : ''"
    >
      <template v-if="showRange" #price-prefix>
        <span class="font-normal">From </span>
      </template>
      <template #rating>
        <slot name="rating" />
      </template>
    </product-title>
    <template v-if="!descriptionBelowCta">
      <tml-render-html-editor-output
        v-if="!descriptionWordLimited"
        :html="product.description"
        class="mb-4"
      />
      <div v-else>
        <tml-details
          :string="product.description"
          class="mb-4 tml-product-page-description-styling"
          align="right"
        />
      </div>
    </template>

    <tml-loader :loading="!showForm" loading-height="12em" class="mb-4">
      <slot name="personalization" :color="activeProductColor" />
      <tml-form
        v-if="showForm"
        ref="productOptions"
        form-name="product-options"
        persistent
      >
        <tml-new-form v-if="showForm" name="product-options-new">
          <template v-if="!product.isBundle">
            <div
              v-if="productColors.length > 1"
              class="flex items-center justify-between mb-2"
            >
              <p>
                {{ colorValue ? 'Colour:' : 'Select colour:' }}
                <b v-if="colorValue">{{ colorValue.value }}</b>
              </p>
              <button
                v-if="product.sizeChartId && disableSizeSelector"
                class="flex items-center gap-1"
                @click.stop.prevent="openSizeChart"
              >
                <font-awesome-icon
                  :icon="faRulerHorizontal"
                  class="text-[--tml-text-color]"
                />
                <span class="text-[--tml-text-color]"> Sizing </span>
              </button>
            </div>
            <tml-color-selector
              v-show="productColors.length > 1"
              v-model="colorValue"
              :pre-selected="colorPreSelected"
              field-name="color"
              :items-per-row="{
                xs:
                  theme().get('productPage.color.selector.border.radius') ===
                  '999px'
                    ? 7
                    : 6,
                md: 4,
                lg:
                  theme().get('productPage.color.selector.border.radius') ===
                  '999px'
                    ? 7
                    : 6,
              }"
              :class="{
                'mb-6':
                  theme().get('productPage.quantitySelector.visible') === '1' &&
                  !showSizeSelector,
              }"
              :colors="productColors"
              :validation-rules="{required: true}"
              :border-radius="
                theme().get('productPage.color.selector.border.radius')
              "
              @input="$emit('color-selected', $event?.value)"
            />

            <div
              v-if="showSizeSelector"
              class="flex items-center justify-between mb-2"
              :class="{'mt-4': productColors.length > 1}"
            >
              <p>
                {{ sizeValue ? 'Size:' : 'Select size:' }}
                <b v-if="sizeValue">{{ sizeValue.value }}</b>
              </p>
              <button
                v-if="product.sizeChartId"
                class="flex items-center gap-1"
                @click.stop.prevent="openSizeChart"
              >
                <font-awesome-icon
                  :icon="faRulerHorizontal"
                  class="text-[--tml-text-color]"
                />
                <span class="text-[--tml-text-color]"> Sizing </span>
              </button>
            </div>
            <div
              v-else-if="
                product.sizeChartId &&
                (!isPurchaseable || overrideAddToCart) &&
                !product.customiseIt &&
                !disableSizeSelector
              "
              class="text-right"
            >
              <button
                class="items-center gap-1 mt-3 inline"
                @click.stop.prevent="openSizeChart"
              >
                <font-awesome-icon
                  :icon="faRulerHorizontal"
                  class="text-[--tml-text-color] pr-1"
                />
                <span class="text-[--tml-text-color]"> Sizing </span>
              </button>
            </div>
            <tml-size-selector
              v-if="showSizeSelector"
              class="mb-4"
              :value="sizeValue"
              field-name="size"
              :sizes="formSelectSizeOptions"
              :items-per-row="sizeSelectorBreakpoints"
              :stock-levels="stockLevels"
              @input="sizeValue = $event"
            />
            <template
              v-if="
                theme().get('productPage.quantitySelector.visible') === '1' &&
                showQuantitySelector
              "
            >
              <tml-new-select
                v-if="quantityDisplayMode === 'selector'"
                name="quantity-select"
                label="Quantity"
                :options="quantityOptions"
                :value="
                  quantityOptions.find(q => q.value === quantity) ||
                  quantityOptions.find(q => q.value === '10+')
                "
                @input="onSelectQuantity"
              >
              </tml-new-select>
              <tml-input
                v-show="quantityDisplayMode === 'input'"
                v-model="quantity"
                type="number"
                class="my-0"
                field-name="quantity_input"
                placeholder="Quantity"
              />
            </template>

            <div
              v-if="giftWrapOption && product.giftWrapCompatible"
              class="mb-4 flex flex-row items-center justify-start"
            >
              <tml-image :src="giftWrapOption.image" class="w-24 h-24 mr-4" />
              <tml-radio-button
                :input-value="giftWrapSelected"
                class="flex flex-row items-center justify-start"
                field-name="gift-wrap"
                :label="`Gift wrap this item (${currency}${formatCurrency(
                  giftWrapOption.price
                )})`"
                @update="value => (giftWrapSelected = value)"
              >
                <template #label-after>
                  Gift wrap this item (<tml-currency
                    :value="giftWrapOptions?.[0]?.price"
                    no-rounding
                  />)
                </template>
              </tml-radio-button>
            </div>
          </template>

          <bundle-item-list
            v-if="shouldShowBundleItems"
            :product="product"
            @input="setBundleOptions"
          />
        </tml-new-form>
      </tml-form>

      <div v-if="shouldShowDispatchDays" class="my-4">
        <font-awesome-icon class="mr-2" :icon="faClock" />
        <span>{{ dispatchDaysMessage }}</span>
      </div>

      <product-stock
        v-else-if="
          currentStock !== null &&
          (product.customiseIt || isPurchaseable) &&
          !personalizationSizes.length // hide the stock for bulk-personalizable products. it would only show the stock for the first selected size
        "
        class="my-4"
        :stock="currentStock"
        :pre-order="preOrder"
      />
    </tml-loader>

    <tml-expandable-container
      v-if="breakdownItems.length > 0"
      title="Price"
      class="my-2"
      no-bottom-margin
    >
      <template #header="{isOpen, onTrigger}">
        <div
          class="flex items-center justify-between py-3 cursor-pointer"
          @click="onTrigger"
        >
          <div class="flex items-center gap-2">
            <span>Price:</span>
            <span class="font-bold" style="color: var(--tml-primary-color)">
              <tml-currency :value="breakdownTotal" no-rounding />
            </span>
          </div>
          <font-awesome-icon
            :icon="faCaretDown"
            class="transition-transform duration-300"
            :class="{'rotate-180': isOpen}"
          />
        </div>
      </template>
      <ul class="list-none p-0 m-0 mb-2">
        <li
          v-for="(item, index) in fullBreakdownItems"
          :key="index"
          class="flex items-center justify-between py-1 text-sm"
        >
          <span
            >{{ item.label
            }}<template v-if="item.quantity > 1">
              (x{{ item.quantity }})</template
            ></span
          >
          <span class="font-bold" style="color: var(--tml-primary-color)">
            {{ currency }}{{ item.totalPrice.toFixed(2) }}
          </span>
        </li>
      </ul>
    </tml-expandable-container>

    <transition name="fade-up" mode="out-in">
      <template v-if="isPurchaseable">
        <tml-button
          v-if="(isInStock || preOrder) && overrideAddToCart"
          class="w-full add-to-bag-button"
          :disabled="product.addButtonDisabled"
          primary
          :loading="loading"
          href="#"
          text="Add to bag"
          @click="onCustomAddToCart"
        />
        <tml-button
          v-else-if="isInStock || preOrder"
          class="w-full add-to-bag-button"
          :disabled="product.addButtonDisabled"
          :primary="!productComingSoon"
          :loading="loading"
          href="#"
          :text="!productComingSoon ? 'Add to bag' : 'Coming Soon'"
          @click="onAddToCart"
        />

        <div v-else-if="!product.isBundle">
          <template v-if="!subscribedToStockNotification">
            <p class="mb-4">Notify me when it's back in-stock</p>
            <tml-form form-name="product-stock-notification" class="mb-4">
              <tml-input
                v-model="subscribeEmail"
                field-name="email"
                placeholder="Email"
                :validation-rules="{required: true, email: true}"
              />
            </tml-form>
            <tml-button
              class="w-full"
              primary
              :loading="loading"
              :disabled="!subscribeEmail"
              href="#"
              text="Notify Me"
              @click="onSubscribeToStock"
            />
            <tml-re-captcha ref="captcha" action="subdomains_oos_notify" />
          </template>
          <template v-else>
            <p class="mb-4">Great, we'll email you when it's back in stock</p>
          </template>
          <small>
            Learn more in our
            <tml-anchor href="/privacy-policy/" text="Privacy Policy" /> /
            <tml-anchor href="/terms-of-use/" text="Terms" />
          </small>
        </div>
      </template>
    </transition>

    <div v-if="product.customiseIt" class="customise-it mt-4">
      <svg
        style="width: 0; height: 0; position: absolute"
        aria-hidden="true"
        focusable="false"
      >
        <linearGradient id="rainbow-gradient" x2="0" y2="1">
          <stop stop-color="#f44336" offset="0%" />
          <stop stop-color="#FFC107" offset="17%" />
          <stop stop-color="#4CAF50" offset="33%" />
          <stop stop-color="#00BCD4" offset="50%" />
          <stop stop-color="#2196F3" offset="67%" />
          <stop stop-color="#9C27B0" offset="84%" />
          <stop stop-color="#f44336" offset="100%" />
        </linearGradient>
      </svg>
      <tml-button
        class="w-full customise-button"
        :href="
          colorValue
            ? `/design-your-own-t-shirt/${product.urlName}/?colour=${colorValue.value}`
            : `/design-your-own-t-shirt/${product.urlName}/`
        "
        :text="$t('productPage.customiseButton')"
        :icon="faPaintbrush"
      />
    </div>

    <div v-if="!product.customiseIt && !isPurchaseable">
      <tml-button class="w-full" primary href="/enquire" text="Enquire" />
    </div>

    <eco-icons v-if="product.ecoIcons.length" :product="product" class="my-8" />
    <div v-else class="mt-4" />

    <div v-if="paymentProcessorType === 'teemill'" class="flex flex-col gap-2">
      <p class="flex items-center">
        <span class="w-8">
          <font-awesome-icon :icon="faThumbsUp" />
        </span>
        Easy Returns and Exchanges
      </p>
      <p class="flex items-center">
        <span class="w-8">
          <font-awesome-icon :icon="faTruckFast" />
        </span>
        Next Day Delivery Available
      </p>
    </div>
    <template v-if="descriptionBelowCta">
      <tml-render-html-editor-output
        v-if="!descriptionWordLimited"
        :html="product.description"
        class="mt-8"
      />

      <tml-details
        v-else
        :string="product.description"
        class="tml-product-page-description-styling mt-8"
        align="right"
      />
    </template>
  </div>
</template>

<script>
import {defineAsyncComponent} from 'vue';
import {faThumbsUp} from '@fortawesome/pro-light-svg-icons/faThumbsUp';
import {faTruckFast} from '@fortawesome/pro-light-svg-icons/faTruckFast';
import {faClock} from '@fortawesome/pro-light-svg-icons/faClock';
import {faRulerHorizontal} from '@fortawesome/pro-light-svg-icons/faRulerHorizontal';
import {faPaintbrush} from '@fortawesome/pro-light-svg-icons/faPaintbrush';
import {faCaretDown} from '@fortawesome/pro-solid-svg-icons/faCaretDown';

import {formatUrl, viewportSize} from '@teemill/common/helpers';
import {usePaymentProcessorStore} from '@teemill/common/vuex';
import {tracker} from '@teemill/common/plugins';

import {favourites} from '@teemill/common';
import {forms} from '@teemill/common/plugins';
import {snackbar, useGtm} from '@teemill/common/services';
import ProductStock from './ProductStock.vue';
import EcoIcons from './EcoIcons.vue';
import {splitTests} from '@teemill/common/classes';
import {
  TmlBubble,
  TmlNewProductColorSelector,
  TmlRadioButton,
  TmlImage,
  TmlCurrency,
  TmlExpandableContainer,
} from '@teemill/components';
import {useAddToBag, useNotifications} from '@teemill/modules/notifications';
import ProductTitle from './ProductTitle.vue';
import {formatCurrency} from '@teemill/utilities';
import BundleItemList from './BundleItemList.vue';
import {pickBy} from 'lodash';

export default {
  name: 'ProductInfo',

  components: {
    BundleItemList,
    EcoIcons,
    ProductStock,
    TmlBubble,
    TmlNewProductColorSelector,
    TmlRadioButton,
    TmlCurrency,
    TmlImage,
    TmlExpandableContainer,
    ProductTitle,
  },

  inject: ['theme'],

  props: {
    product: Object,
    showForm: {
      type: Boolean,
      default: true,
    },
    overrideAddToCart: Boolean,
    showFromMinPrice: Boolean,
    disableSizeSelector: Boolean,
    disableQuantitySelector: Boolean,
    selectedColor: String,
    addonPrice: {
      type: Number,
      default: 0,
    },
    addonActiveRows: {
      type: Number,
      default: 0,
    },
    breakdownItems: {
      type: Array,
      default: () => [],
    },
    personalizationSizes: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      faTruckFast,

      splitTests,

      quantity: 1,

      sizeValue: null,
      colorValue: null,
      faThumbsUp,
      faRulerHorizontal,
      faPaintbrush,
      faClock,
      faCaretDown,
      loading: false,
      subscribedToStockNotification: false,

      colorPreSelected: '',

      bundleOptions: [],

      bundleOptionOutOfStock: false,

      favourites,
      subscribeEmail: '',

      giftWrapSelected: false,

      quantityDisplayMode: 'selector',
      quantityOptions: [
        ...Array.from({length: 9}, (_, i) => i + 1).map(i => ({
          text: i,
          value: i,
        })),
        {
          text: '10+',
          value: '10+',
        },
      ],
    };
  },

  computed: {
    usesNewProductFeed() {
      return this.$store.state.subdomain.usesNewProductFeed;
    },

    paymentProcessorType() {
      return usePaymentProcessorStore().getter('paymentProcessorType');
    },

    isMobile() {
      return viewportSize.isSmaller('md');
    },

    isPurchaseable() {
      return this.product.enableAddToCart ?? true;
    },

    descriptionBelowCta() {
      return (
        this.theme().get('productPage.description.placement') ===
        'belowSelectors'
      );
    },

    descriptionWordLimited() {
      return this.theme().get('productPage.description.word.limited') === '1';
    },

    isInStock() {
      return this.currentStock > 0 || this.currentStock === null;
    },

    selectedColour() {
      return this.colorValue?.value;
    },

    selectedSize() {
      return this.$store.getters['forms/product-options/formData']?.size?.value;
    },

    currency() {
      return this.$store.getters['currency/get'](null)?.symbol;
    },

    selectedBundleOptions() {
      return this.bundleOptions.map(bundleOption => ({
        color: bundleOption.colour,
        size: bundleOption.size,
      }));
    },

    productColors() {
      const options = this.product.options;

      return Object.keys(options).map(colour => ({
        text: colour,
        value: colour,
        name: colour,
        background: options[colour].thumbnail
          ? this.colorBackground(options[colour].thumbnail)
          : undefined,
      }));
    },

    activeProductColor() {
      return this.productColors.find(
        colour => colour.value === this.selectedColour
      );
    },

    currentStock() {
      const size = this.sizeValue || this.onlySize;

      if (this.selectedColour && size?.value) {
        return this.product.options[this.selectedColour].sizes[size.value].stock
          ?.quantity;
      } else if (this.product.isBundle && !this.shouldShowBundleItems) {
        return this.lowestBundleItemStock;
      }

      return null;
    },

    lowestBundleItemStock() {
      let minimumStock = null;

      if (this.product.bundleOptions) {
        Object.values(this.product.bundleOptions).forEach(bundleOption => {
          Object.values(bundleOption).forEach(colour => {
            Object.values(colour.sizes).forEach(size => {
              const stock = size?.stock?.quantity ?? size;
              if (
                stock !== null &&
                (stock < minimumStock || minimumStock === null)
              ) {
                minimumStock = stock;
              }
            });
          });
        });
      }

      return minimumStock;
    },

    shouldShowBundleItems() {
      return (
        this.product.isBundle &&
        (this.productHasBundleItemsWithMoreThanOneColour ||
          this.productHasBundleItemsWithMoreThanOneSize)
      );
    },

    productHasBundleItemsWithMoreThanOneColour() {
      return this.product.bundleItems
        .map(bundleItem => bundleItem.options)
        .some(option => Object.keys(option).length > 1);
    },

    productHasBundleItemsWithMoreThanOneSize() {
      return Object.values(
        this.product.bundleItems.map(bundleItem => bundleItem.options)
      )
        .map(option => Object.values(option))
        .map(option => option.map(subOption => subOption.sizes))
        .some(optionSizesPerColor =>
          optionSizesPerColor.some(
            optionSizes => Object.values(optionSizes).length > 1
          )
        );
    },

    formSelectSizeOptions() {
      const options = [];

      if (this.selectedColour) {
        this.productSizes(this.selectedColour).forEach(size => {
          options.push({
            text: size,
            value: size,
          });
        });
      }

      return options;
    },

    productComingSoon() {
      if (this.product.approvalStatus === 'denied') {
        return true;
      }

      return false;
    },

    showSizeSelector() {
      if (this.disableSizeSelector) {
        return false;
      }

      if (!this.isPurchaseable) {
        return false;
      }

      return (
        this.selectedColour &&
        (this.formSelectSizeOptions.length > 1 || this.product.sizeCount > 1)
      );
    },

    onlySize() {
      return this.formSelectSizeOptions.length === 1
        ? this.formSelectSizeOptions[0]
        : null;
    },

    showQuantitySelector() {
      if (this.disableQuantitySelector) {
        return false;
      }

      return true;
    },

    stockLevels() {
      return Object.keys(this.product.options[this.colorValue.value].sizes).map(
        key => {
          return this.product.options[this.colorValue.value].sizes[key];
        }
      );
    },

    preOrder() {
      const product = this.product;

      if (product.isBundle) {
        return false;
      }

      const size = this.sizeValue || this.onlySize;

      if (this.selectedColour && size?.value) {
        return product.options[this.selectedColour].sizes[size.value]?.stock
          ?.preOrder;
      }

      return false;
    },

    dispatchDays() {
      const product = this.product;

      if (product.isBundle) {
        return this.getBundleDispatchDays();
      }

      if (this.personalizationSizes.length) {
        return this.getPersonalizationDispatchDays();
      }

      const colour = this.getColourForDispatchDays();
      const size = this.getSizeForDispatchDays(colour);

      if (colour && size) {
        const option = product.options?.[colour]?.sizes?.[size];

        return option?.stock?.dispatchDays ?? 0;
      }

      return 0;
    },

    shouldShowDispatchDays() {
      if (this.dispatchDays <= 0) {
        return false;
      }

      if (this.personalizationSizes.length || this.product.isBundle) {
        return false;
      }

      const hasSelectedColour =
        this.productColors.length <= 1 ? true : Boolean(this.selectedColour);

      if (!hasSelectedColour) {
        return false;
      }

      const requiresSizeSelection =
        this.formSelectSizeOptions.length > 1 || this.product.sizeCount > 1;

      if (!requiresSizeSelection) {
        return true;
      }

      return Boolean(this.getSelectedSize());
    },

    hasApplications() {
      return Boolean(
        this.product.personalizationTemplate ||
          this.product.applications?.length
      );
    },

    dispatchDaysMessage() {
      if (this.hasApplications) {
        return `Personalised items can take up to ${this.dispatchDays} days`;
      }

      return `Ships out in ${this.dispatchDays} days`;
    },

    activePrice() {
      const product = this.product;

      if (product.isBundle) {
        return product.price;
      }

      if (this.colorValue && this.sizeValue) {
        return product.options[this.colorValue.value].sizes[
          this.sizeValue.value
        ].price;
      }

      if (this.colorValue) {
        const colorOption = product.options?.[this.colorValue.value];
        if (colorOption?.sizes) {
          const prices = Object.values(colorOption.sizes)?.map(
            size => size.price
          );

          if (prices?.length) {
            return Math.min(...prices);
          }
        }
      }

      return product.price;
    },

    // true if one variant has been selected (i.e. colour and size selected) and not a bulk-personalizable product
    singleActiveVariantSelected() {
      if (this.product.isBundle) {
        return true;
      }

      if (
        this.colorValue &&
        this.sizeValue &&
        !this.personalizationSizes.length
      ) {
        return true;
      }

      return false;
    },

    salePrice() {
      const product = this.product;

      if (product.isBundle) {
        return product.salePrice;
      }

      if (this.colorValue && this.sizeValue) {
        return product.options[this.colorValue.value].sizes[
          this.sizeValue.value
        ].salePrice;
      }

      if (this.colorValue) {
        const colorOption = product.options?.[this.colorValue.value];
        if (colorOption?.sizes) {
          const salePrices = Object.values(colorOption.sizes)?.map(
            size => size.salePrice
          );

          if (salePrices?.length) {
            return Math.min(...salePrices);
          }
        }
      }

      return product.salePrice;
    },

    minOptionPrice() {
      const product = this.product;
      const allPrices = [];

      Object.values(product.options).forEach(colorOption => {
        if (colorOption?.sizes) {
          Object.values(colorOption.sizes).forEach(size => {
            if (size.price !== null) {
              allPrices.push(size.price);
            }
          });
        }
      });

      if (allPrices.length) {
        return Math.min(...allPrices);
      }

      return product.price;
    },

    minOptionSalePrice() {
      const product = this.product;
      const allSalePrices = [];

      Object.values(product.options).forEach(colorOption => {
        if (colorOption?.sizes) {
          Object.values(colorOption.sizes).forEach(size => {
            if (size.salePrice !== null) {
              allSalePrices.push(size.salePrice);
            }
          });
        }
      });

      if (allSalePrices.length) {
        return Math.min(...allSalePrices);
      }

      return product.salePrice;
    },

    showRange() {
      if (this.salePrice > 0 || this.singleActiveVariantSelected) {
        return false;
      }

      return this.maxOptionPrice > this.minOptionPrice && this.showFromMinPrice;
    },

    maxOptionPrice() {
      const product = this.product;
      const allPrices = [];

      Object.values(product.options).forEach(colorOption => {
        if (colorOption?.sizes) {
          Object.values(colorOption.sizes).forEach(size => {
            if (size.price !== null) {
              allPrices.push(size.price);
            }
          });
        }
      });

      if (allPrices.length) {
        return Math.max(...allPrices);
      }

      return product.price;
    },

    titlePrice() {
      if (!this.isPurchaseable && !this.product.customiseIt) {
        return null;
      }

      if (this.singleActiveVariantSelected) {
        return this.activePrice;
      }

      if (this.showFromMinPrice) {
        return this.minOptionPrice;
      }

      return this.activePrice;
    },

    titleSalePrice() {
      if (!this.isPurchaseable && !this.product.customiseIt) {
        return null;
      }

      if (this.showFromMinPrice) {
        return this.minOptionSalePrice;
      }

      return this.salePrice;
    },

    fullBreakdownItems() {
      if (this.breakdownItems.length === 0) {
        return [];
      }

      const items = [];

      if (this.addonActiveRows === 0 && this.sizeValue) {
        items.push({
          label: `Size ${this.sizeValue.value}`,
          quantity: 1,
          unitPrice: this.activePrice,
          totalPrice: this.activePrice,
        });
      }

      items.push(...this.breakdownItems);
      return items;
    },

    breakdownTotal() {
      return this.fullBreakdownItems.reduce(
        (sum, item) => sum + item.totalPrice,
        0
      );
    },

    longestSizeName() {
      if (!this.formSelectSizeOptions.length) {
        return '';
      }

      return this.formSelectSizeOptions.reduce((a, b) =>
        a.text.length > b.text.length ? a : b
      ).text;
    },

    sizeSelectorBreakpoints() {
      return this.longestSizeName.length > 6
        ? {
            xs: 3,
            md: 2,
            lg: 3,
          }
        : {
            xs: 6,
            md: 4,
            lg: 6,
          };
    },

    giftWrapOptions() {
      return (
        this.$store.state.subdomain?.giftWrapOptions?.flatMap(product => {
          return product.variants.map(variant => {
            return {
              id: variant.id,
              price: variant.price,
              image: variant.image,
            };
          });
        }) || []
      );
    },

    giftWrapOption() {
      return this.giftWrapOptions?.length ? this.giftWrapOptions[0] : null;
    },
  },

  watch: {
    colorValue(newColour) {
      this.resetSizeIfUnavailableFor(newColour?.value);
    },

    selectedColor: {
      handler(newColor) {
        if (newColor && newColor !== this.colorValue?.value) {
          const colorOption = this.productColors.find(
            color => color.value === newColor
          );
          if (colorOption) {
            this.colorValue = colorOption;
          }
        }
      },
      immediate: true,
    },

    product: {
      handler() {
        let preSelectedColor = this.productColors[0];

        let defaultColorName = this.product.lifestyleImages[0]?.color;

        if (this.$route.params.colour) {
          defaultColorName = decodeURIComponent(this.$route.params.colour)
            .replace(/-/, ' ')
            .replace(/\+/, '-')
            .replace(/(\b[a-z](?!\s))/g, x => x.toUpperCase());
        }

        if (defaultColorName) {
          const matchedColor = this.productColors.find(
            colour => colour.name === defaultColorName
          );

          if (matchedColor) {
            preSelectedColor = matchedColor;
          }
        }

        this.colorPreSelected = preSelectedColor;
      },
      immediate: true,
    },

    formSelectSizeOptions(newValue, oldValue) {
      if (!oldValue.length && this.$route.query['options[]']) {
        let options = this.$route.query['options[]'];
        if (!Array.isArray(options)) {
          options = [options];
        }

        options.forEach(option => {
          const [key, value] = option.split(':');
          if (key === 'Size') {
            this.sizeValue =
              this.formSelectSizeOptions.find(size => size.value === value) ??
              this.sizeValue;
          }
        });
      }
    },

    personalizationSizes(newValue, oldValue) {
      this.sizeValue = newValue.length ? {value: newValue[0]} : this.sizeValue;
    },
  },

  methods: {
    getColourForDispatchDays() {
      return (
        this.selectedColour ||
        this.colorPreSelected?.value ||
        this.productColors[0]?.value
      );
    },

    getSizeForDispatchDays(colour) {
      const size = this.sizeValue || this.onlySize;

      if (size?.value) {
        return size.value;
      }

      if (!colour) {
        return null;
      }

      const sizes = Object.keys(this.product.options?.[colour]?.sizes || {});

      return sizes.length ? sizes[0] : null;
    },

    getBundleDispatchDays() {
      if (!this.product.bundleOptions) {
        return 0;
      }

      const selectionDispatchDays = this.bundleOptions
        .filter(option => option?.id && option?.colour && option?.size)
        .map(option => this.getBundleOptionDispatchDays(option))
        .filter(days => days > 0);

      return selectionDispatchDays.length
        ? Math.max(...selectionDispatchDays)
        : 0;
    },

    getBundleOptionDispatchDays(option) {
      if (!option?.id || !option?.colour || !option?.size) {
        return 0;
      }

      const stock =
        this.product.bundleOptions?.[option.id]?.[option.colour]?.sizes?.[
          option.size
        ]?.stock;

      return stock?.dispatchDays ?? 0;
    },

    getPersonalizationDispatchDays() {
      const colour = this.getColourForDispatchDays();

      if (!colour) {
        return 0;
      }

      const dispatchDays = this.personalizationSizes
        .map(size => this.getDispatchDaysForSize(colour, size))
        .filter(days => days > 0);

      return dispatchDays.length ? Math.max(...dispatchDays) : 0;
    },

    getDispatchDaysForSize(colour, size) {
      const option = this.product.options?.[colour]?.sizes?.[size];

      return option?.stock?.dispatchDays ?? 0;
    },

    onSelectQuantity(event) {
      if (event.value === '10+') {
        this.quantityDisplayMode = 'input';
        this.quantity = 10;
      } else {
        this.quantity = event.value;
      }
    },

    productSizes(colour) {
      const sizes = this.product.options[colour]?.sizes;

      if (sizes === undefined) {
        return [];
      }

      const filteredSizes = pickBy(sizes, size => size.price > 0);
      return Object.keys(filteredSizes);
    },

    openSizeChart() {
      useGtm().trackEvent({
        event: 'onOpenSizeChart',
      });

      this.$overlays.open(`size-popup-${this.product.id}`);
    },

    validateAddToCart() {
      return new Promise((resolve, reject) => {
        if (!this.productComingSoon && !this.product.addButtonDisabled) {
          const productOptionsForm =
            this.$store.getters['forms/product-options/formData'];

          if (
            (this.showSizeSelector && !this.sizeValue) ||
            (this.bundleOptions.length &&
              this.bundleOptions.map(option => option.size).includes(undefined))
          ) {
            reject('Please choose a size');
            return;
          }

          if (!this.quantity || this.quantity < 1) {
            reject('Please select a quantity');
            return;
          }

          forms
            .validate('product-options-new')
            .success(() => {
              if (productOptionsForm) {
                const productOptionsFormIsValid =
                  this.$store.getters['forms/product-options/isValid'];

                const selectedColor = this.selectedColour;

                const selectedSize = this.getSelectedSize();

                this.$refs.productOptions.validateAll();

                if (!productOptionsFormIsValid) {
                  if (this.product.isBundle) {
                    reject('Please choose a size and colour for all items');
                  } else {
                    reject('Please choose a size and colour ');
                  }
                  return;
                }

                if (this.bundleItemOutOfStock) {
                  const bundleItem = this.bundleItemOutOfStock;
                  reject(
                    `Looks like ${bundleItem.name} (${bundleItem.size}) in ${bundleItem.colour} is out of stock`
                  );
                  return;
                }

                resolve({
                  color: selectedColor,
                  size: selectedSize,
                });
              }
            })
            .catch(() => {
              if (this.product.isBundle) {
                reject('Please choose a size and colour for all items');
              } else {
                reject('Please choose a size and colour ');
              }
            });
        }
      });
    },

    onAddToCart() {
      this.validateAddToCart()
        .then(({color, size}) => {
          this.loading = true;

          this.$store
            .dispatch('cart/addItem', {
              item: {
                productId: this.product.id,
                productName: this.product.name,
                productColor: color,
                productSize: size,
                productPrice: this.activePrice,
                bundleOptions: this.bundleOptions,
                quantity: this.quantity,
                giftWrapOptionId: this.giftWrapSelected
                  ? this.giftWrapOption?.id
                  : null,
              },
              name: 'main',
            })
            .then(response => {
              this.onAfterAddToCart(response);

              this.sendAddToCartEvent(color, size);

              this.loading = false;
            })
            .catch(() => {
              this.loading = false;
            });
        })
        .catch(e => snackbar(e));
    },

    onCustomAddToCart() {
      this.validateAddToCart()
        .then(({color, size}) => {
          this.loading = true;
          this.$emit('custom-add-to-cart', {
            color,
            size,
            response: data => {
              this.loading = false;
              this.onAfterAddToCart(data, color, size);

              this.sendAddToCartEvent(color, size);
            },
            reject: () => {
              this.loading = false;
            },
          });
        })
        .catch(e => snackbar(e));
    },

    onAfterAddToCart(response, color = null, size = null) {
      if (response?.items?.length > 0) {
        tracker.conversion('add-to-cart', this.activePrice, [this.product.id]);

        const productTypeEventName = this.product.isBundle
          ? 'add-bundle-to-cart'
          : 'add-single-product-to-cart';

        tracker.event(productTypeEventName);

        this.showAddedToBagNotification(color, size);
      }
    },

    showAddedToBagNotification(color = null, size = null) {
      this.dismissSubscribeOfferNotification();

      useNotifications().removeAll();

      const itemPrice =
        this.breakdownItems.length > 0 ? this.breakdownTotal : this.activePrice;

      const addToBagData = {
        bagCount: this.$store.getters['cart/getQuantity'],
        items: [
          {
            name: this.product.name,
            price: itemPrice,
            salePrice: this.salePrice,
          },
        ],
      };

      if (this.product.isBundle) {
        addToBagData.items = addToBagData.items.map(item => ({
          ...item,
          image: this.product.lifestyleImages[0]['120'],
          options: this.selectedBundleOptions,
        }));
      } else {
        addToBagData.items = addToBagData.items.map(item => ({
          ...item,
          image: this.product.options[this.selectedColour].flatImage['120'],
          quantity: this.quantity,
          options: [
            {
              color:
                color ||
                (this.formSelectSizeOptions.length > 1 &&
                this.productColors.length > 1
                  ? this.selectedColour
                  : undefined),
              size: size || this.getSelectedSize(),
            },
          ],
        }));
      }

      const {setRouter, show} = useAddToBag();
      setRouter(this.$router);
      show(addToBagData);
    },

    dismissSubscribeOfferNotification() {
      const {handle, notifications} = useNotifications();

      const subscribeOfferNotification = notifications.find(
        notification => notification.id === 'subscriber-offer-discount'
      );

      if (subscribeOfferNotification) {
        handle(
          subscribeOfferNotification,
          'action://plugins/subscriber-offer/dismiss'
        );
      }
    },

    sendAddToCartEvent() {
      let productOptions = [];

      if (this.product.isBundle) {
        productOptions = this.selectedBundleOptions;
      } else {
        productOptions.push({
          color: this.selectedColour,
          size: this.getSelectedSize(),
        });
      }

      const productPrice = this.salePrice || this.activePrice;

      useGtm().addToCart({
        contentType: 'product',
        contentIds: [
          this.usesNewProductFeed ? this.product.uuid : this.product.id,
        ],
        items: [
          {
            id: this.usesNewProductFeed ? this.product.uuid : this.product.id,
            name: this.product.name,
            brand: JSON.stringify(this.$store.state.subdomain.company.name),
            price: productPrice,
            currency: this.$store?.state?.subdomain?.currency ?? 'GBP',
            dimension4: `teemill:${this.$store.state.subdomain.divisionName}`,
            quantity: 1,
            options: productOptions,
          },
        ],
        currency: this.$store?.state?.subdomain?.currency ?? 'GBP',
        value: productPrice,
        dimension4: `teemill:${this.$store.state.subdomain.divisionName}`,
        userId: this.$store.state.subdomain.sessionId,
      });
    },

    onSubscribeToStock() {
      if (this.$store.getters['forms/product-stock-notification/isValid']) {
        if (this.loading) {
          return;
        }

        this.loading = true;

        const productOptionsForm =
          this.$store.getters['forms/product-options/formData'];
        const selectedColor = productOptionsForm.color.value.value;
        const selectedSize = this.getSelectedSize();

        const getFieldValue =
          this.$store.getters['forms/product-stock-notification/getFieldValue'];
        const email =
          typeof getFieldValue === 'function'
            ? getFieldValue('email', 'value')
            : this.$store.getters['forms/product-stock-notification/formData']
                ?.email?.value;

        if (!email) {
          this.loading = false;
          return;
        }

        this.$refs.captcha.recaptcha
          .ifHuman(recaptcha => {
            this.axios
              .post(
                formatUrl('/omnis/v3/frontend/notification/subscribeToStock/'),
                {
                  email,
                  productId: this.product.id,
                  productColor: selectedColor,
                  productSize: selectedSize,
                  v3captchaToken: recaptcha.v3Token,
                  v2captchaToken: recaptcha.v2Token,
                }
              )
              .success(() => {
                this.subscribedToStockNotification = true;
              })
              .validation()
              .oops()
              .any(() => {
                if (this.$refs.captcha) {
                  this.$refs.captcha.recaptcha.reload();
                }
              })
              .finally(() => {
                this.loading = false;
              });
          })
          .ifBot(() => {
            this.loading = false;
          });
      }
    },

    colorBackground(thumbnail) {
      switch (thumbnail?.type) {
        case 'image':
          return `url(${formatUrl(thumbnail.value)})`;
        case 'color':
          return thumbnail.value;
        default:
          return undefined;
      }
    },

    setBundleOptions(value) {
      this.bundleOptions = value;

      this.checkBundleStock();
    },

    checkBundleStock() {
      this.bundleItemOutOfStock = null;

      this.bundleOptions.forEach(bundle => {
        if (bundle.stock === 0) {
          this.bundleItemOutOfStock = {
            name: bundle.name,
            size: bundle.size,
            colour: bundle.colour,
          };
        }
      });
    },

    async attemptToFavourite(identifier) {
      try {
        await this.favourites.add(identifier);
      } catch (error) {
        snackbar.error(error.message);
      }
    },

    getSelectedSize() {
      if (this.formSelectSizeOptions.length === 1) {
        return this.formSelectSizeOptions[0].value;
      } else {
        return this.product.isBundle ? null : this.sizeValue?.value;
      }
    },

    resetSizeIfUnavailableFor(colour) {
      if (!this.sizeValue) {
        return;
      }

      const sizesForColour = this.productSizes(colour);

      if (!sizesForColour) {
        this.sizeValue = null;
      }

      if (!sizesForColour.includes(this.sizeValue?.value)) {
        if (sizesForColour.length < 1) {
          this.sizeValue = null;
        } else {
          const size = sizesForColour[sizesForColour.length - 1];

          this.sizeValue = {
            value: size,
            text: size,
            name: size,
          };
        }
      }
    },

    formatCurrency,
  },
};
</script>

<style lang="scss">
.tml-product-info {
  .customise-button svg path {
    fill: url(#rainbow-gradient) #f44336;
  }

  .favourite-icon {
    width: 3em;
    height: 2.75em;
    margin-left: 0.5em;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer !important;

    svg {
      color: var(--tml-text-color);
      height: 1.75em;
    }
  }
}
.tml-product-page-description-styling {
  p {
    margin-bottom: 1em;
    word-break: break-word;
  }

  h1,
  h2,
  h3 {
    margin-bottom: 0;
    line-height: 2em !important;
  }

  li > p {
    margin-bottom: 0em;
  }

  li {
    margin-left: 1.5em;
    list-style-type: circle;
  }

  blockquote {
    margin: 1em 0;
    font-size: 1.25em;
    font-style: italic;

    p {
      margin: 0;
    }
  }
}
</style>

<style lang="scss" scoped>
.fade-up-enter-active {
  animation: fade-up 0.3s;
}
.fade-up-leave-active {
  animation: fade-up 0.3s reverse;
}

@keyframes fade-up {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
